import { ClearComponent } from './ClearComponent';
import {
  AppleLogo,
  GoogleLogo,
  HideIcon,
  SingpassIcon,
  UnHideIcon,
  SingpassTextIcon,
} from '@/assets/svg';
import CustomCountryCodeInput from '@/new-components/CustomCountryCodeInput';
import CustomLoadingButton from '@/new-components/CustomLoadingButton';
import {
  Box,
  IconButton,
  InputAdornment,
  InputLabel,
  makeStyles,
  TextField,
  Typography,
  withStyles,
} from '@material-ui/core';
import { Divider } from 'antd';
import React, { useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';

const CustomLink = withStyles({
  root: {
    fontWeight: 600,
    color: '#AD5E99',
    cursor: 'pointer',
  },
})(Typography);

const CustomTypography = withStyles({
  root: {
    color: '#666666',
  },
})(Typography);

const CustomTitle = withStyles({
  root: {
    fontSize: 32,
    fontFamily: 'Lato',
    fontWeight: 700,
    color: '#191919',
  },
})(Typography);

const CustomInputLabel = withStyles({
  root: {
    color: '#666666',
    marginBottom: 8,
    fontWeight: 600,
  },
})(InputLabel);

const CustomSingpassButton = withStyles({
  root: {
    maxWidth: 335,
    backgroundColor: '#F4333D',
    borderRadius: 16,
    padding: 15,
    paddingBottom: 12,
  },
})(IconButton);
const CustomTextField = withStyles({
  root: {
    fontSize: 20,

    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      height: 48,
    },
    '& .MuiOutlinedInput-input': {
      marginRight: 0,
      paddingLeft: 5,
      fontSize: 16,
    },

    '& .MuiInputBase-inputMultiline': {
      '&.MuiInputBase-input': {
        marginLeft: 0,
      },
    },

    '& .Mui-disabled': {
      '&.MuiOutlinedInput-root': {
        backgroundColor: '#f6f6f6',
      },
    },
    '& .MuiInputBase-adornedStart': {
      '& .MuiTypography-root': {
        fontSize: 16,
        color: '#333333',
      },
      '& .MuiInputAdornment-positionStart': {
        marginRight: 0,
      },
      '& input': {
        marginLeft: 0,
      },
    },
    '& .MuiFormHelperText-root': {
      alignItems: 'center',
      display: 'flex',
      fontFamily: 'OpenSans',
      fontSize: 12,
      gap: 6,
      marginLeft: 2,

      '& .MuiSvgIcon-root': {
        fontSize: 'unset',
      },
    },
  },
})(TextField);

const useStyles = makeStyles(() => ({
  divider: {
    color: '#999 !important',
    fontWeight: 'normal !important',
    fontSize: '14px !important',
    lineHeight: '20px !important',
    fontFamily: 'OpenSans !important',
    margin: '0 !important',

    '&::before': {
      borderColor: '#E0E0E0 !important',
    },
    '&::after': {
      borderColor: '#E0E0E0 !important',
    },
  },
  appleBtn: {
    width: 64,
    height: 64,
    backgroundColor: '#000',
    color: '#fff',
    borderRadius: 16,
    '& svg': {
      width: 40,
      height: 40,
    },
    '&:hover': {
      color: '#000',
    },
  },
  googleBtn: {
    width: 64,
    height: 64,
    border: '1px solid #E0E0E0',
    borderRadius: 16,
    '& svg': {
      width: 36,
      height: 36,
    },
  },
}));

const AccountInfo = ({
  errors,
  formValue,
  goLogin,
  handleChangeForm,
  handleSubmit,
  loading,
  confirmPassword,
  setConfirmPassword,
  handleClearForm,
  handleCountryCode,
  handleSingpassLogin,
  handleAppleLogin,
  handleGoogleLogin,
}) => {
  const classes = useStyles();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isEmptyParams = useMemo(() => {
    let filterKeys = ['email', 'password', 'phoneNumber'];
    let checkValid =
      filterKeys.some((key) => !formValue[key]) || !confirmPassword;

    return checkValid;
  }, [confirmPassword, formValue]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      height={'100%'}
    >
      <Box textAlign="center">
        <CustomTitle>Sign up</CustomTitle>

        <form>
          <Box maxWidth={600} textAlign="left" mt={5}>
            <CustomInputLabel>Email</CustomInputLabel>
            <CustomTextField
              id="email"
              fullWidth
              variant="outlined"
              placeholder="email@address.com"
              autoComplete="email"
              value={formValue.email || ''}
              onChange={handleChangeForm('email')}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                endAdornment: (
                  <ClearComponent
                    id="email"
                    value={formValue.email}
                    onClear={() => handleClearForm('email')}
                  />
                ),
              }}
            />
            <>
              <CustomInputLabel style={{ marginTop: 24 }}>
                Password
              </CustomInputLabel>
              <CustomTextField
                id="password"
                fullWidth
                variant="outlined"
                placeholder="12345678a"
                autoComplete="password"
                value={formValue.password || ''}
                onChange={handleChangeForm('password')}
                type={showPassword ? '' : 'password'}
                error={!!errors.password}
                helperText={
                  errors.password ||
                  'Password must be 8-32 characters long and includes at least one number, one letter (uppercase or lowercase), and may contain special characters like !@#$%^&*(),_-+='
                }
                InputProps={{
                  endAdornment: (
                    <>
                      <ClearComponent
                        id="password"
                        value={formValue.password}
                        onClear={() => handleClearForm('password')}
                      />
                      <InputAdornment position="start">
                        <>
                          <HideIcon
                            style={{ cursor: 'pointer' }}
                            hidden={showPassword}
                            onClick={() => setShowPassword(true)}
                          />
                          <UnHideIcon
                            style={{ cursor: 'pointer' }}
                            hidden={!showPassword}
                            onClick={() => setShowPassword(false)}
                          />
                        </>
                      </InputAdornment>
                    </>
                  ),
                }}
              />
              <CustomInputLabel style={{ marginTop: 24 }}>
                Confirm password
              </CustomInputLabel>
              <CustomTextField
                id="confirmPassword"
                fullWidth
                variant="outlined"
                placeholder="12345678a"
                autoComplete="password"
                value={confirmPassword || ''}
                onChange={(e) => setConfirmPassword(e.target.value)}
                type={showConfirmPassword ? '' : 'password'}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <>
                      <ClearComponent
                        id="confirmPassword"
                        value={confirmPassword}
                        onClear={() => handleClearForm('confirmPassword')}
                      />

                      <InputAdornment position="start">
                        <>
                          <HideIcon
                            style={{ cursor: 'pointer' }}
                            hidden={showConfirmPassword}
                            onClick={() => setShowConfirmPassword(true)}
                          />
                          <UnHideIcon
                            style={{ cursor: 'pointer' }}
                            hidden={!showConfirmPassword}
                            onClick={() => setShowConfirmPassword(false)}
                          />
                        </>
                      </InputAdornment>
                    </>
                  ),
                }}
              />
            </>

            <CustomInputLabel style={{ marginTop: 24 }}>
              Phone number
            </CustomInputLabel>
            <Box display="flex" gridGap={8}>
              <CustomCountryCodeInput
                country={formValue.countryCode}
                onChange={handleCountryCode}
                isMobileSize
              />
              <NumberFormat
                id="phoneNumber"
                fullWidth
                customInput={CustomTextField}
                variant="outlined"
                placeholder="1234 5678"
                onValueChange={handleChangeForm('phoneNumber')}
                allowNegative={false}
                value={formValue.phoneNumber || ''}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                InputProps={{
                  endAdornment: (
                    <ClearComponent
                      id="phoneNumber"
                      value={formValue.phoneNumber}
                      onClear={() => handleClearForm('phoneNumber')}
                    />
                  ),
                }}
              />
            </Box>
          </Box>

          <CustomLoadingButton
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading || isEmptyParams}
            loading={loading}
            style={{ marginTop: 24, width: '100%' }}
          >
            Sign up
          </CustomLoadingButton>
        </form>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        gridGap={24}
        alignSelf="center"
        py={3}
        width="100%"
      >
        <Divider className={classes.divider}>Or continue with</Divider>
        <Box display="flex" gridGap={16} justifyContent="center">
          <IconButton onClick={handleAppleLogin} className={classes.appleBtn}>
            <AppleLogo />
          </IconButton>
          <IconButton onClick={handleGoogleLogin} className={classes.googleBtn}>
            <GoogleLogo />
          </IconButton>
          <CustomSingpassButton onClick={handleSingpassLogin}>
            <SingpassTextIcon />
          </CustomSingpassButton>
        </Box>

        <Box display="flex" gridGap={6} justifyContent="center">
          <CustomTypography>Already have an account?</CustomTypography>{' '}
          <CustomLink onClick={goLogin}>Sign in</CustomLink>
        </Box>
      </Box>
    </Box>
  );
};

export default AccountInfo;
